import {
  DvIconCellRenderer,
  DvToolbarAction,
  DvToolbarActionEvent,
} from '@devinco/ui/grid';
import { Injectable, inject } from '@angular/core';
import { RestApiService } from '@core/services/rest-api.service';
import { ColumnType } from '@features/vt-grid/grid-options/column-types';
import { ToolbarActionsService } from '@features/vt-grid/services/toolbar-actions.service';
import { Entity } from '@shared/constants/entity';
import { TranslateService } from '@ngx-translate/core';
import VoltageUtils from '@shared/utils/voltage.utils';
import { ColDef, IRowNode } from 'ag-grid-community';
import { of, switchMap, take, tap } from 'rxjs';
import { AssetDto } from '@pages/assets-page/components/interfaces';
import { FindUpdateDialogType } from '@pages/assets-page/components/constants';
import { UpdateAssetFindDialogService } from './update-asset-find-dialog/update-asset-find-dialog.service';
import NextServiceUtils from '@shared/utils/next-service.utils';
import { HardwareDeviceStatus } from '@shared/constants/hardware-device-status';
import { DvSplitterEventsService } from '@devinco/ui/dds/splitter';
import { getStandardActionPreset } from '@features/vt-grid/presets/preset-actions';
import { ThumbnailRendererParams } from '@shared/components/thumbnail-renderer/interfaces';

@Injectable({ providedIn: 'root' })
export class AssetsGridService {
  private toolbarActionService = inject(ToolbarActionsService);
  private splitterEvents = inject(DvSplitterEventsService);
  private translate = inject(TranslateService);
  private assetFindUpdateDialogService = inject(UpdateAssetFindDialogService);
  private api = inject(RestApiService);

  getColDefs = (): ColDef<AssetDto>[] => [
    {
      headerName: 'Asset.Picture',
      field: 'DocumentThumbnail',
      type: [ColumnType.Thumbnail],
      cellRendererParams: () => {
        return {
          attachmentIdKey: 'ImageDocumentID',
          enablePreviewOnClick: true,
        } satisfies Partial<ThumbnailRendererParams>;
      },
      enableRowGroup: false,
      sortable: false,
    },
    {
      field: 'Description',
      headerName: 'Asset.Description',
      type: [ColumnType.Text],
      width: 300,
    },
    {
      field: 'Serial',
      headerName: 'Asset.SerialNo',
      type: [ColumnType.NumberText],
      width: 70,
    },
    {
      field: 'Voltage',
      headerName: 'Asset.Voltage',
      type: [ColumnType.Number],
      width: 100,
      cellStyle: ({ value }) => {
        return VoltageUtils.setCellColor(value);
      },
      valueFormatter: formatterParams => {
        return VoltageUtils.voltageValueFormatter(formatterParams);
      },
    },
    {
      field: 'LastActivityTime',
      headerName: 'Asset.LastActivityTime',
      type: [ColumnType.DateTime],
    },
    {
      field: 'CurrentLocation',
      headerName: 'Asset.CurrentLocation',
      type: [ColumnType.Text],
    },
    {
      field: 'LastMoved',
      headerName: 'Asset.LastMoved',
      type: [ColumnType.DateTime],
    },
    {
      field: 'PreviousLocation',
      headerName: 'Asset.PreviousLocation',
      type: [ColumnType.Text],
    },
    {
      field: 'LastKnownLocation',
      headerName: 'Asset.LastKnownLocation',
      width: 250,
      type: [ColumnType.Text],
    },
    {
      headerName: 'Asset.Temperature',
      field: 'Temperature',
      type: [ColumnType.Number],
    },
    {
      field: 'Comment',
      headerName: 'Asset.Comment',
      type: [ColumnType.Text],
    },
    {
      type: [ColumnType.Status],
      headerName: 'Asset.Status',
      hide: true,
      cellRenderer: DvIconCellRenderer,
      cellRendererParams: (params: {
        data: AssetDto | undefined;
        value: number | undefined;
      }) => {
        const findActiveValue = params.data?.FindActive;
        if (
          findActiveValue === HardwareDeviceStatus.Inactive ||
          findActiveValue === false
        ) {
          return {
            icon: 'do_not_disturb_on',
            color: 'var(--error500)',
            value: true,
          };
        }
        return VoltageUtils.setVoltageStatusIcon(params.data?.Voltage);
      },
    },
    {
      field: 'AssetSerial',
      headerName: 'Asset.AssetSerial',
      type: [ColumnType.NumberText],
      hide: true,
    },
    {
      field: 'AssetTypeDescription',
      headerName: 'Asset.Type',
      type: [ColumnType.Text],
      hide: true,
    },
    {
      field: 'Username',
      headerName: 'Asset.OwnedByUser',
      type: [ColumnType.Text],
      hide: true,
    },
    {
      field: 'Personal',
      headerName: 'Asset.Private',
      type: [ColumnType.Checkbox],
      hide: true,
      width: 60,
      filterParams: {
        values: [true, false],
        valueFormatter: (data: { value: boolean }) => {
          const key =
            data.value === true ? 'Asset.IsPrivate' : 'Asset.IsNotPrivate';
          return this.translate.instant(key);
        },
      },
      filter: 'agSetColumnFilter',
    },
    {
      field: 'Department',
      headerName: 'Asset.Department',
      type: [ColumnType.Text],
      cellClass: ({ data }) =>
        data?.DepartmentDeleted ? 'vt-deleted-row' : '',
      hide: true,
    },
    {
      field: 'ShelfLocation',
      headerName: 'Asset.ShelfLocation',
      width: 110,
      hide: true,
      type: [ColumnType.Text],
    },
    {
      field: 'PurchaseDate',
      hide: true,
      headerName: 'Asset.PurchaseDate',
      type: [ColumnType.DateVT],
    },
    {
      field: 'LastInspection',
      hide: true,
      headerName: 'Asset.LastInspection',
      type: [ColumnType.DateVT],
    },
    {
      field: 'NextInspection',
      hide: true,
      headerName: 'Asset.NextInspection',
      type: [ColumnType.DateVT],
      cellStyle: ({ value }) => {
        if (!value) return undefined;

        return {
          'background-color': NextServiceUtils.getNextServiceDateBg(value),
          'text-align': 'right',
        };
      },
    },
    {
      field: 'LastGPSLat',
      headerName: 'Asset.LastGPSLat',
      type: [ColumnType.Number],
      hide: true,
    },
    {
      field: 'LastGPSLon',
      headerName: 'Asset.LastGPSLon',
      type: [ColumnType.Number],
      hide: true,
    },
    {
      headerName: 'Asset.AssetID',
      field: 'AssetID',
      type: [ColumnType.Id],
      hide: true,
    },
    {
      headerName: 'Asset.TempOutOfOrder',
      field: 'TemporaryInactive',
      type: [ColumnType.Checkbox],
      filterParams: {
        values: [true, false],
        valueFormatter: (data: { value: boolean }) => {
          const key = data.value ? 'Asset.TempOutOfOrder' : 'Asset.InOrder';
          return this.translate.instant(key);
        },
      },
      filter: 'agSetColumnFilter',
      hide: true,
    },
    {
      headerName: 'Asset.Active',
      field: 'FindActive',
      type: [ColumnType.Checkbox],
      hide: true,
      filterParams: {
        values: [true, false],
        valueFormatter: (data: { value: boolean }) => {
          const key = data.value ? 'Asset.ActiveFind' : 'Asset.InactiveFind';
          return this.translate.instant(key);
        },
      },
      valueGetter: ({ data }) => {
        if (typeof data?.FindActive === 'boolean') {
          return data.FindActive;
        }

        switch (data?.FindActive) {
          case null:
          case undefined:
            return data?.FindActive;
          case HardwareDeviceStatus.Active:
            return true;
          case HardwareDeviceStatus.Inactive:
            return false;
        }
      },
      filter: 'agSetColumnFilter',
    },
    {
      headerName: 'Asset.LinkedDocumentCount',
      field: 'LinkedDocumentCount',
      type: [ColumnType.Number],
      hide: true,
    },
    {
      headerName: 'Asset.NotificationLostTime',
      field: 'NotificationLostTime',
      type: [ColumnType.Checkbox],
      valueGetter: params => !!params.data?.NotificationLostTime,
      filterParams: {
        valueFormatter: (data: { value: boolean }) => {
          const key = data.value
            ? 'Asset.NotificationLostTime'
            : 'Asset.DontNotificationLostTime';
          return this.translate.instant(key);
        },
      },
      filter: 'agSetColumnFilter',
      hide: true,
    },
    {
      headerName: 'Asset.NotifyNotInCar',
      field: 'NotificationLocationLeaving',
      type: [ColumnType.Checkbox],
      filterParams: {
        values: [true, false],
        valueFormatter: (data: { value: boolean }) => {
          const key = data.value
            ? 'Asset.NotifyNotInCar'
            : 'Asset.DontNotifyNotInCar';
          return this.translate.instant(key);
        },
      },
      filter: 'agSetColumnFilter',
      hide: true,
    },
    {
      field: 'VisibleToDepartmentText',
      headerName: 'Asset.VisibleToDepartment',
      type: [ColumnType.Text],
      hide: true,
    },
    { field: 'ImageDocumentID', type: ColumnType.Invisible },
    { field: 'DepartmentDeleted', type: ColumnType.Invisible },
  ];

  getGridActions(showOnMap: () => void): DvToolbarAction[] {
    return [
      ...getStandardActionPreset(),
      {},
      {
        icon: 'map_marker_move',
        tooltipKey: 'AssetsGrid.ShowAssetOnMap',
        clickFn: showOnMap,
        disabled: true,
      },
      {
        icon: 'delete',
        clickFn: params => this.deleteAsset(params),
        disabled: true,
        tooltipKey: 'Grid.Delete',
      },
      {
        icon: 'edit',
        disabled: true,
        tooltipKey: 'Grid.Edit',
        clickFn: () => this.splitterEvents.isSplitterOpened$.next(true),
      },
    ];
  }

  getAssetByTypeExtraColDefs = (): ColDef[] => [
    {
      field: 'ParentAssetID',
      type: [ColumnType.Text],
      headerName: 'Asset.ParentAssetID',
      hide: true,
    },
    {
      field: 'IsAsset',
      headerName: 'Asset.IsAsset',
      type: [ColumnType.Checkbox],
      hide: true,
    },
  ];

  private deleteAsset(params: DvToolbarActionEvent): void {
    const selectedRowData =
      (params.api.getSelectedNodes()[0] as IRowNode<AssetDto>)!.data;
    if (
      selectedRowData?.FindActive === HardwareDeviceStatus.Active ||
      !!selectedRowData?.FindActive
    ) {
      this.assetFindUpdateDialogService
        .openAssetFindUpdateDialog(FindUpdateDialogType.DeleteDialog)
        .closed.pipe(
          switchMap(({ source, responseData }) => {
            if (source !== 'confirm') return of();
            return this.api
              .delete(
                Entity.Asset,
                params.api.getSelectedNodes()[0].data.AssetID,
                this.assetFindUpdateDialogService.getFindUpdateQueryParams(
                  responseData
                )
              )
              .pipe(
                take(1),
                tap(() =>
                  this.toolbarActionService.selectRowAfterDelete(params.api)
                )
              );
          }),
          take(1)
        )
        .subscribe();
    } else {
      this.toolbarActionService.deleteSelectedRow(
        params,
        undefined,
        Entity.Asset
      );
    }
  }
}
